import { Button, Flex, HStack, Icon, Stack, Text, useBreakpointValue } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeftIcon, ChevronRightIcon } from '../../../assets/icons'

interface PaginationProps {
  currentPage: number
  totalItems: number
  itemsPerPage: number
  maxPage: number
  handlePreviousPage: () => void
  handleNextPage: () => void
  handlePageClick: (page: number) => void
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  maxPage,
  handlePreviousPage,
  handleNextPage,
  handlePageClick,
}) => {
  const { t } = useTranslation()
  const page = currentPage || 1

  const maxVisiblePages = useBreakpointValue({ base: 3, sm: 5 }) ?? 3
  const pagesBeforeCurrent = Math.floor((maxVisiblePages - 1) / 2)
  let startPage = Math.max(1, page - pagesBeforeCurrent)
  const endPage = Math.min(maxPage, startPage + maxVisiblePages - 1)

  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1)
  }
  const pageNumbers: number[] = []
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i)
  }

  return (
    <Flex
      justifyContent="space-between"
      alignItems={{ base: 'stretch', sm: 'center' }}
      direction={{ base: 'column', sm: 'row' }}
      gap={{ base: 1, sm: 4 }}
      w="100%"
      px={{ base: 3, sm: 0 }}
      py={{ base: 2, sm: 0 }}
    >
      <HStack ml={{ base: 0, sm: 5 }} justify={{ base: 'center', sm: 'flex-start' }}>
        <Text textStyle="b-xs" color="solid.500">
          {t('pagination.showing', {
            start: page * itemsPerPage - itemsPerPage + 1,
            end: Math.min(page * itemsPerPage, totalItems),
            total: totalItems,
          })}
        </Text>
      </HStack>

      <Flex alignItems="center" justify={{ base: 'center', sm: 'flex-end' }} gap={2} mr={{ base: 0, sm: 5 }} overflowX="auto">
        <HStack pb={{ base: 1, sm: 3 }} pt={{ base: 1, sm: 3 }} minW="max-content">
          <Button
            onClick={() => {
              if (page > 1) handlePreviousPage()
            }}
            disabled={page === 1}
            color={page === 1 ? 'solid.300' : 'solid.900'}
            border="none"
          >
            <Icon width="18px" height="18px" viewBox="0 0 24 24" color={page === 1 ? 'solid.300' : 'solid.900'}>
              <ChevronLeftIcon />
            </Icon>
            {t('common.actions.previous')}
          </Button>

          <Stack direction="row" spacing="8px">
            {pageNumbers.map((p) => (
              <Button
                key={p}
                variant={p === page ? 'paginationActive' : 'pagination'}
                onClick={() => handlePageClick(p)}
                _focus={{
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                }}
                _hover={{
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                }}
                _active={{
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                }}
                border="none"
                outline="none"
              >
                <Text textStyle="h-xs">{p}</Text>
              </Button>
            ))}
          </Stack>

          <Button
            onClick={() => {
              if (page < maxPage) handleNextPage()
            }}
            disabled={page === maxPage}
            color={page === maxPage ? 'solid.300' : 'solid.900'}
            border="none"
          >
            {t('common.actions.next')}
            <Icon width="18px" height="18px" viewBox="0 0 24 24" color={page === maxPage ? 'solid.300' : 'solid.900'}>
              <ChevronRightIcon />
            </Icon>
          </Button>
        </HStack>
      </Flex>
    </Flex>
  )
}

export default Pagination
